import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';

import { skip, Subscription, filter } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { UserAccountService } from '../../../services/user-account/user-account.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import {
  getSearchVendorStatusLabel,
  isBackgroundRefreshPending,
  isVendorSyncComplete,
} from '../../../services/search-polling/osint-job-poll-response.util';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { AdvancedSearchService } from '../../../services/advanced/advanced-search.service';
import { CountriesService } from '../../../services/countries/countries.service';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';

@Component({
  selector: 'app-advanced-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective,
    CreditsLabelPipe,
  ],
  templateUrl: './advanced-search.component.html',
  styleUrl: './advanced-search.component.css',
})
export class AdvancedSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;
  searchName: string = '';
  searchPhone: string = '';
  searchEmail: string = '';
  searchAddress: string = '';
  searchIdNumber: string = '';
  // selectedCountryCode: string = '+91';
  // countries: any[] = [];
  // selectedCountry: any = null;
  // showCountryDropdown: boolean = false;
  allJobResultsSubscription?: Subscription;
  routerEventsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];
  searchLoading = false;
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;
  recentResultsSelectAll = { isSelected: false };
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;
  private searchServicesSubscription?: Subscription;

  selectedFilterType: string = 'name';

  filterTypes: any = [
    { label: 'Name', value: 'name' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';

  nameFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.name_search_id);
  }

  /** UI-only: count of non-empty search fields (max 5) for footer “selectors used”. */
  get filledSelectorCount(): number {
    let n = 0;
    if (this.searchName?.trim()) n++;
    if (this.searchPhone?.trim()) n++;
    if (this.searchEmail?.trim()) n++;
    if (this.searchAddress?.trim()) n++;
    if (this.searchIdNumber?.trim()) n++;
    return n;
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private searchPollingService: SearchPollingService,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    private advancedSearchService: AdvancedSearchService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) // public countriesService: CountriesService,
  { }

  ngOnInit() {
    this.accessDetails = this.retrieveAccessDetails();
    // this.countries = this.countriesService.getAllCountries();
    // this.selectedCountry =
    //   this.countries.find((c) => c.dialCode === this.selectedCountryCode) ||
    //   this.countries[0];
    // Check if Name (Advanced Search) service is disabled
    this.searchServicesSubscription =
      this.searchServiceStatusService.searchServices$.subscribe(
        (searchServices) => {
          if (searchServices) {
            this.isServiceDisabled = !searchServices.name;
          }
        },
      );

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'nameSearchCreditCost',
      );
    });

    this.getRecentResults();
    this.getAvailableCredits();

    this.subscribeToJobResultsUpdates();

    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (
          event.url === '/advanced-search' ||
          event.url.startsWith('/advanced-search?')
        ) {
          this.getRecentResults();
        }
      });
  }

  // toggleCountryDropdown() {
  //   this.showCountryDropdown = !this.showCountryDropdown;
  // }

  // selectCountry(country: any) {
  //   this.selectedCountry = country;
  //   this.selectedCountryCode = country.dialCode;
  //   this.showCountryDropdown = false;
  // }

  // @HostListener('document:click', ['$event'])
  // onDocumentClick(event: Event) {
  //   const target = event.target as HTMLElement;
  //   if (
  //     !target.closest('.country-select-wrapper') &&
  //     !target.closest('.country-dropdown')
  //   ) {
  //     this.showCountryDropdown = false;
  //   }
  // }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              result.done = updatedStatus.done || false;
              result.status = updatedStatus.status || 'pending';
              result.background_refresh_pending = Boolean(
                updatedStatus.background_refresh_pending,
              );
              result.vendor_sync_complete = isVendorSyncComplete(updatedStatus);
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
    this.searchServicesSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  searchNameSubmit() {
    const validationResult = this.validateSearchInputs();
    if (!validationResult.valid) {
      this.toastr.error(
        validationResult.error || 'At least one search field is required',
      );
      return;
    }
    if (this.searchLoading) {
      return;
    }
    this.searchLoading = true;
    const { ip, location } = this.retrieveIPAndLocation();

    const searchPayload: any = {
      device: 'Chrome',
    };

    if (ip != '') {
      searchPayload.ip_address = ip;
    }

    if (location != '') {
      searchPayload.location = location;
    }

    // Add only non-empty fields to the payload
    if (this.searchName.trim()) {
      searchPayload.name = this.searchName.trim().replace(/\s+/g, '');
    }
    if (this.searchPhone.trim()) {
      // Add country code to phone number if not already present
      const phoneNumber = this.searchPhone.trim().replace(/\s+/g, '');
      // const phoneWithCountryCode = phoneNumber.startsWith('+')
      //   ? phoneNumber
      //   : this.selectedCountryCode + phoneNumber;
      // searchPayload.phone = phoneWithCountryCode;
      searchPayload.phone = phoneNumber;
    }
    if (this.searchEmail.trim()) {
      searchPayload.email = this.searchEmail.trim().replace(/\s+/g, '');
    }
    if (this.searchAddress.trim()) {
      searchPayload.address = this.searchAddress.trim().replace(/\s+/g, '');
    }
    if (this.searchIdNumber.trim()) {
      searchPayload.id_number = this.searchIdNumber.trim().replace(/\s+/g, '');
    }

    this.advancedSearchService.searchName(searchPayload).subscribe(
      (data: any) => {
        this.searchLoading = false;

        const history = data?.data || {};
        const jobId = history?.job_id;
        const nameSearchId = history?.name_search_id;
        if (jobId) {
          this.jobIds = [jobId];
          const label =
            searchPayload.name ||
            searchPayload.phone ||
            searchPayload.email ||
            searchPayload.id_number ||
            searchPayload.address ||
            jobId;

          this.searchPollingService.startPolling(this.jobIds, (jobIds) =>
            this.advancedSearchService.getSearchResults(jobIds),
          );
          this.searchPollingService.ensureBackgroundPolling(
            [
              {
                job_id: jobId,
                searchType: 'advanced',
                label: String(label),
              },
            ],
            3 * 1000,
          );

          // Open result immediately using ids from async start (even without inline name_data).
          this.router.navigate([`/advanced-search-result/${jobId}`], {
            state: {
              searchResult: {
                ...history,
                job_id: jobId,
                name_search_id: nameSearchId,
                searched_name: label,
                name: label,
              },
            },
          });
          return;
        }

        this.getRecentResults();
        this.clearSearchInputs();
      },
      (error) => {
        this.searchLoading = false;
        let errorMessage = 'Unable to Search Name Details';
        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }
        this.toastr.error(errorMessage);
      },
    );
  }

  validateSearchInputs(): { valid: boolean; error?: string } {
    const hasName = this.searchName.trim().replace(/\s+/g, '').length > 0;
    const hasPhone = this.searchPhone.trim().replace(/\s+/g, '').length > 0;
    const hasEmail = this.searchEmail.trim().replace(/\s+/g, '').length > 0;
    const hasAddress = this.searchAddress.trim().replace(/\s+/g, '').length > 0;
    const hasIdNumber = this.searchIdNumber.trim().replace(/\s+/g, '').length > 0;

    // At least one field must be provided
    if (!hasName && !hasPhone && !hasEmail && !hasAddress && !hasIdNumber) {
      return { valid: false, error: 'At least one search field is required' };
    }

    // Validate name if provided (2-100 chars)
    if (hasName) {
      const trimmedName = this.searchName.trim().replace(/\s+/g, '');
      if (trimmedName.length < 2) {
        return {
          valid: false,
          error: 'Name must be at least 2 characters long',
        };
      }
      if (trimmedName.length > 100) {
        return { valid: false, error: 'Name must not exceed 100 characters' };
      }
    }

    // Validate phone if provided (max 50 chars)
    if (hasPhone && this.searchPhone.trim().replace(/\s+/g, '').length > 50) {
      return { valid: false, error: 'Phone must not exceed 50 characters' };
    }

    // Validate email if provided (max 255 chars)
    if (hasEmail && this.searchEmail.trim().replace(/\s+/g, '').length > 255) {
      return { valid: false, error: 'Email must not exceed 255 characters' };
    }

    // Validate address if provided (max 255 chars)
    if (hasAddress && this.searchAddress.trim().replace(/\s+/g, '').length > 255) {
      return { valid: false, error: 'Address must not exceed 255 characters' };
    }

    // Validate id_number if provided (max 50 chars)
    if (hasIdNumber && this.searchIdNumber.trim().replace(/\s+/g, '').length > 50) {
      return { valid: false, error: 'ID Number must not exceed 50 characters' };
    }

    return { valid: true };
  }

  clearSearchInputs() {
    this.searchName = '';
    this.searchPhone = '';
    this.searchEmail = '';
    this.searchAddress = '';
    this.searchIdNumber = '';
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);

        let errorMessage = 'Failed to load available credits';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => { },
    });
  }

  getRecentResults() {
    this.loadingRecentResults = true;
    this.advancedSearchService
      .getNameSearchHistory(
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response: any) => {
          this.loadingRecentResults = false;

          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          this.refreshPendingJobStatuses();
        },
        error: (error: any) => {
          console.error(error);
          this.loadingRecentResults = false;

          let errorMessage = 'Failed to load recent search results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Load Results Failed');
        },
      });
  }

  addAdditionalKeys(recentResults: any) {
    return recentResults.map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateRecentResultPaged(isChecked, recentResultPaged.name_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) => recentResultPaged.name_search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;

    this.getRecentResults();
  }

  onDeleteButtonClick() {
    this.deleteRecentResults();
  }

  deleteRecentResults() {
    this.advancedSearchService
      .deleteNameSearchHistory({
        name_search_ids: this.selectedRecentResults,
      })
      .subscribe({
        next: (response) => {
          this.recentResultsSelectAll.isSelected = false;

          this.updateRecentResultsPaged();
          this.updateRecentResultsCurrentPage();
          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);

          let errorMessage = 'Failed to delete selected results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Delete Failed');
        },
      });
  }

  updateRecentResultsPaged() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.name_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (this.recentResultsHasNextPage) {
      } else {
        if (this.recentResultsHasPreviousPage) {
          this.recentResultsCurrentPage--;
        }
      }
    }
  }

  onFilterTypeChange() {
    this.reset();
  }

  reset() {
    this.recentResultsCurrentPage = 1;

    this.filterKey = '';
    this.filterValue = '';

    this.nameFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          result.done = cachedStatus.done || false;
          result.status = cachedStatus.status || 'pending';
          result.background_refresh_pending = Boolean(
            cachedStatus.background_refresh_pending,
          );
          result.vendor_sync_complete = isVendorSyncComplete(cachedStatus);
        }
      }
    });

    const pendingJobIds = this.recentResultsPaged
      .filter(
        (result: any) =>
          result.job_id && !isVendorSyncComplete(result),
      )
      .map((result: any) => result.job_id)
      .filter((jobId: string) => !!jobId);

    if (pendingJobIds.length > 0) {
      this.searchPollingService
        .checkJobStatus(pendingJobIds, (jobIds) =>
          this.advancedSearchService.getSearchResults(jobIds),
        )
        .subscribe({
          next: (jobResultsMap) => {
            this.recentResultsPaged.forEach((result: any) => {
              if (result.job_id) {
                const updatedStatus = jobResultsMap.get(result.job_id);
                if (updatedStatus) {
                  result.done = updatedStatus.done || false;
                  result.status = updatedStatus.status || 'pending';
                  result.background_refresh_pending = Boolean(
                    updatedStatus.background_refresh_pending,
                  );
                  result.vendor_sync_complete =
                    isVendorSyncComplete(updatedStatus);
                }
              }
            });
          },
          error: (error) => {
            console.warn('Failed to refresh job statuses:', error);
          },
        });
    }
  }

  isRecentRowComplete(row: any): boolean {
    return isVendorSyncComplete(row);
  }

  isRecentRowRefreshing(row: any): boolean {
    return Boolean(row?.done) && isBackgroundRefreshPending(row);
  }

  getRecentRowStatusLabel(row: any): string {
    return getSearchVendorStatusLabel(row);
  }

  getAllJobResults() { }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate([`/advanced-search-result/${searchResult.job_id}`], {
      state: { searchResult },
    });
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onNameFilterChange(name: string) {
    const trimmedName = name.trim();

    if (trimmedName === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedName.length === 1) {
      return;
    } else {
      this.filterKey = 'name';
      this.filterValue = trimmedName;

      this.getRecentResults();
    }
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedNotes.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;

      this.getRecentResults();
    }
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onExportExcelButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToExcel(transformedRecentResultsPaged);
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'Advanced Search ID': recentResultPaged.name_search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCHED NAME': recentResultPaged.searched_name,
      'USER EMAIL': recentResultPaged.user?.email || '',
      'IP ADDRESS': recentResultPaged.ip_address,
      'LOCATION': recentResultPaged.location,
      'DEVICE': recentResultPaged.device,
      'NOTES':
        recentResultPaged.notes === undefined ? null : recentResultPaged.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  exportToExcel(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `advanced-results-${timestamp || 'export'}`;
    this.documentExportService.exportToExcel(
      transformedRecentResultsPaged,
      fileName,
      'Advanced Results',
    );
  }

  onExportCsvButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToCsv(transformedRecentResultsPaged);
  }

  exportToCsv(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `advanced-results-${timestamp || 'export'}`;
    this.documentExportService.exportToCsv(
      transformedRecentResultsPaged,
      fileName,
      'Advanced Results',
    );
  }

  onExportPdfButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToPdf(transformedRecentResultsPaged);
  }

  exportToPdf(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `advanced-results-${timestamp || 'export'}`;
    const title = `Advanced results generated from Ryno`;
    const username = this.getUsername();

    this.documentExportService.exportResultsToPdf(
      transformedRecentResultsPaged,
      fileName,
      title,
      username,
    );
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.user_name || '';
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
